// Pure derivation for the dashboard StatusHero.
//
// Maps the open session + its challenges + per-challenge submission state into
// the presentational pieces the hero renders: a mono eyebrow, a state-driven
// headline (split into lead / italic-teal emphasis / muted tail), a per-challenge
// chip row, and an aggregate submission summary.
//
// All challenges in a session share ONE gate closure (`gateClosureAt`), so there
// is a single countdown; what varies per challenge is horizon/target-day and
// whether it has been submitted.

import {formatTargetDay} from './datetime';

const URGENT_THRESHOLD_MS = 10 * 60 * 1000; // final minutes before gate closure

const toMs = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return value.getTime();
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

const challengeBadge = (c) =>
  c.horizonLabel || (c.horizonOffset != null ? `D+${c.horizonOffset}` : 'Challenge');

/**
 * Format a remaining duration (ms) as "6h 42m" or, under an hour, "42m".
 * Clamps at zero.
 */
export const formatCountdown = (remainingMs) => {
  const totalMin = Math.max(0, Math.floor(remainingMs / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * @returns null when there is no open session to show, otherwise the hero view-model.
 */
export const deriveSessionHero = ({
  sessionStatus,
  challenges = [],
  coverage = {},
  isForecaster = true,
  challengesLoaded = false,
  now = Date.now(),
} = {}) => {
  if (!sessionStatus || !sessionStatus.isOpen || !sessionStatus.sessionId) {
    return null;
  }

  const nowMs = toMs(now) ?? Date.now();
  const gateMs = toMs(sessionStatus.gateClosureAt);
  const remainingMs = gateMs == null ? null : gateMs - nowMs;
  const closed = remainingMs != null && remainingMs <= 0;
  const countdownText = remainingMs == null ? null : formatCountdown(remainingMs);

  const chips = challenges.map((c) => {
    const intraday = c.horizonOffset === 0 || /intraday/i.test(c.horizonLabel || '');
    const cov = coverage?.[c.id] || null;
    return {
      id: c.id,
      badge: challengeBadge(c),
      day: intraday ? '24h' : formatTargetDay(c.targetDay),
      ready: !!cov?.ready,
      source: cov?.source ?? null,
      startDatetime: c.startDatetime ?? null,
      endDatetime: c.endDatetime ?? null,
      typeLabel: c.challengeTypeLabel || null,
    };
  });

  const total = chips.length;
  const submitted = chips.filter((c) => c.ready).length;
  const due = total - submitted;

  const observer = !isForecaster;
  // Loaded-and-empty: this resource has no challenges in the open session, so
  // countdown/submit framing would mislead. While the fetch is in flight
  // (challengesLoaded false) the generic branches below act as the fallback.
  const noChallenges = challengesLoaded && total === 0;
  const urgent =
    !observer && !closed && remainingMs != null && remainingMs < URGENT_THRESHOLD_MS && due > 0;

  let headline;
  if (noChallenges) {
    headline = {
      lead: 'This session has ',
      emph: 'no challenges',
      tail: ' for this resource.',
    };
  } else if (observer) {
    // Admins / market owners don't submit — show a neutral session state,
    // no "due"/"last call"/submit framing.
    headline = closed
      ? {lead: 'Forecast window ', emph: 'closed', tail: '.'}
      : {lead: 'Forecast window ', emph: 'open', tail: countdownText ? `. Closes in ${countdownText}.` : '.'};
  } else if (closed) {
    headline = {
      lead: total > 1 ? 'Forecasts ' : 'Forecast ',
      emph: 'locked',
      tail: '. Scores land after settlement.',
    };
  } else if (urgent) {
    headline = {
      lead: 'Last call — ',
      emph: countdownText,
      tail: ` for ${due} forecast${due > 1 ? 's' : ''}.`,
    };
  } else if (total === 0) {
    headline = {
      lead: 'Session ',
      emph: 'open',
      tail: countdownText ? `. Closes in ${countdownText}.` : '.',
    };
  } else if (due === 0) {
    headline =
      total === 1
        ? {lead: 'Your forecast is ', emph: 'ready', tail: `. Window closes in ${countdownText}.`}
        : {lead: `All ${total} forecasts are `, emph: 'ready', tail: `. Window closes in ${countdownText}.`};
  } else if (due === total) {
    headline =
      total === 1
        ? {lead: 'Your forecast is ', emph: 'pending', tail: `. ${countdownText} left to submit.`}
        : {lead: `${total} forecasts are `, emph: 'pending', tail: `. ${countdownText} left to submit.`};
  } else {
    headline = {
      lead: `${due} of ${total} forecasts still `,
      emph: 'pending',
      tail: `. Window closes in ${countdownText}.`,
    };
  }

  return {
    sessionId: sessionStatus.sessionId,
    eyebrow: `SESSION #${sessionStatus.sessionId}`,
    headline,
    chips,
    total,
    submitted,
    due,
    submittedLabel: observer || total === 0 ? null : `${submitted} of ${total} ready`,
    countdownText,
    closed,
    urgent,
    observer,
    noChallenges,
    dot: urgent ? 'amber' : 'green',
  };
};
